//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PluginAllcard from './AllPluginCard';
import mockData from '../../mockData.json';
import { useConfig } from '../../Config';

const cardsData = mockData.map((item) => ({
  title: item.title,
  text: item.content,
  buttonLink: item.link || '',
  nameLabel: item.appName,
  plugintype: item.plugintype,
  updated: item.createdAt.split('T')[0],
  provider: item.provider || '',
  content: item.content,
  createdAt: item.createdAt,
  link: item.link,
  imageUrl: item.imageUrl,
}));

interface AllPuginPageProps {
  onViewDetails: (plugin: any) => void;
}

function AllPuginPage({ onViewDetails }: AllPuginPageProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string | null>(null);
  const { services } = useConfig();

  useEffect(() => {
    queueMicrotask(() => setFilter(null));
  }, []);

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter(event.target.value || null);
  };

  const sortedCardsData = cardsData.sort((a, b) =>
    a.title.localeCompare(b.title),
  );

  const filteredCards = sortedCardsData.filter((card) => {
    const matchesFilter = filter
      ? card.nameLabel.toLowerCase() === filter.toLowerCase()
      : true;
    return matchesFilter;
  });

  return (
    <div id="id-all-plugins-container" data-testid="all-plugins-container">
      <div
        id="id-all-plugins-header"
        data-testid="all-plugins-header"
        className="mb-4 flex items-center justify-between"
      >
        <h2 className="text-base font-medium text-[#4a4a56] dark:text-[#c8c8dd]">
          {t('allPlugins')}
        </h2>
        <div
          id="id-all-plugins-filter"
          data-testid="all-plugins-filter"
          className="flex items-center gap-2"
        >
          <label
            htmlFor="filter-solutions"
            className="text-sm text-gray-600 dark:text-gray-400"
          >
            Filter by Solutions
          </label>
          <select
            id="filter-solutions"
            onChange={handleFilterChange}
            className="appearance-none rounded border border-gray-300 bg-white px-3 py-1.5 pr-8 text-sm text-gray-700 outline-none focus:border-[#0F62FE] focus:ring-2 focus:ring-[#0F62FE] dark:border-gray-600 dark:bg-[#262626] dark:text-gray-300 dark:focus:border-[#78A9FF] dark:focus:ring-[#78A9FF]"
          >
            <option value="">{t('allApplications')}</option>
            <option value="plan">{t('devOpsPlan')}</option>
            <option value="deploy">{t('devOpsDeploy')}</option>
            <option value="measure">{t('devOpsMeasure')}</option>
            <option value="test">{t('devOpsTest')}</option>
            <option value="control">{t('devOpsControl')}</option>
            <option value="build">{t('devOpsBuild')}</option>
          </select>
        </div>
      </div>
      <div
        data-testid="all-plugins-grid"
        className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4"
      >
        {filteredCards.map((card, index) => {
          const provider = services?.provider?.name || '';
          const link =
            provider === 'IBM'
              ? 'https://github.com/UrbanCode/IBM-UCx-PLUGIN-DOCS'
              : provider === 'HCL'
                ? 'https://plugins.hcltechsw.com/'
                : 'https://default-link.com';
          return (
            <PluginAllcard
              key={index}
              title={card.title}
              text={card.text}
              buttonLink={link}
              nameLabel={card.nameLabel}
              plugintype={card.plugintype}
              updated={card.updated}
              provider={provider}
              appName={''}
              content={card.content}
              createdAt={card.createdAt}
              link={link}
              imageUrl={card.imageUrl}
              onViewDetails={() => onViewDetails(card)}
            />
          );
        })}
      </div>
    </div>
  );
}

export default AllPuginPage;
